import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

export const SITE_SECTION_TABS = [
  { id: "header", icon: "bi-house-door" },
  { id: "about", icon: "bi-file-earmark-text" },
  { id: "contact", icon: "bi-calendar3" },
  { id: "social", icon: "bi-share" },
  { id: "brand", icon: "bi-palette" },
];

const pillClass = (active) =>
  `inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3.5 text-sm font-semibold whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    active
      ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200"
      : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
  }`;

/** Room kept beside a tab scrolled into view, so the arrow does not cover it. */
const EDGE_ROOM = 48;

/**
 * One end of the row while there is more of it that way: a fade over the pills
 * that runs off, and an arrow that pages to them.
 *
 * The arrow is for a pointer or a thumb and is hidden from assistive tech — the
 * tab list already reaches every section with the arrow keys, and a second,
 * unlabelled way to the same place would only be noise there.
 */
const EdgeArrow = ({ side, onClick }) => (
  <div
    className={`pointer-events-none absolute inset-y-0 flex items-center ${
      side === "end" ? "right-0 bg-linear-to-l pl-6" : "left-0 bg-linear-to-r pr-6"
    } from-ink-50 from-45% to-transparent`}
  >
    <button
      type="button"
      tabIndex={-1}
      aria-hidden="true"
      onClick={onClick}
      className="pointer-events-auto inline-flex size-11 cursor-pointer items-center justify-center"
    >
      <span className="inline-flex size-8 items-center justify-center rounded-full bg-white text-ink-700 shadow-card ring-1 ring-ink-200">
        <i className={`bi ${side === "end" ? "bi-chevron-right" : "bi-chevron-left"}`} />
      </span>
    </button>
  </div>
);

EdgeArrow.propTypes = {
  side: PropTypes.oneOf(["start", "end"]).isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * One pill per section of the public page. A tab list, so the arrow keys move
 * between them.
 *
 * On a phone the row is wider than the screen and scrolls sideways inside
 * itself rather than wrapping into a block of pills taller than the panel under
 * it. A bare scroll row hid that there was anything past "Contact" — and the
 * browser's own scrollbar, drawn over the row, sat across the pills — so the
 * scrollbar is hidden and whichever end has more gets a fade and an arrow
 * instead. The open tab is kept in view too, including one opened from the
 * preview.
 */
const SiteSectionTabs = ({ active, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const refs = useRef({});
  const scrollerRef = useRef(null);
  const [more, setMore] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = { start: el.scrollLeft > 1, end: el.scrollLeft < max - 1 };
    setMore((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next,
    );
  }, []);

  // The row's width changes with the window and its content with the language,
  // and neither is a scroll event.
  useEffect(() => {
    measure();
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    const el = scrollerRef.current;
    const tab = refs.current[active];
    if (!el || !tab) return;
    const from = tab.offsetLeft - EDGE_ROOM;
    const to = tab.offsetLeft + tab.offsetWidth + EDGE_ROOM - el.clientWidth;
    const left = el.scrollLeft > from ? from : el.scrollLeft < to ? to : null;
    if (left !== null) el.scrollTo?.({ left, behavior: "smooth" });
  }, [active]);

  const page = (direction) => {
    const el = scrollerRef.current;
    el?.scrollBy?.({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  };

  const move = (event, index) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    const edge = { Home: 0, End: SITE_SECTION_TABS.length - 1 }[event.key];
    if (step === undefined && edge === undefined) return;
    event.preventDefault();
    const next =
      edge ?? (index + step + SITE_SECTION_TABS.length) % SITE_SECTION_TABS.length;
    const { id } = SITE_SECTION_TABS[next];
    onChange(id);
    refs.current[id]?.focus();
  };

  return (
    // The negative margin lets the row run to the screen edge on a phone and,
    // from `sm`, leaves 4px either side so the open pill's ring and the focus
    // outline are not clipped by the scroller — they were, on the first pill,
    // which then looked shifted off the content edge.
    <div className="relative -mx-4 sm:-mx-1">
      <div
        ref={scrollerRef}
        onScroll={measure}
        className="relative overflow-x-auto px-4 py-1 [scrollbar-width:none] sm:px-1 [&::-webkit-scrollbar]:hidden"
      >
        <div
          role="tablist"
          aria-label={t("Dashboard.MySite.sections.label")}
          className="flex w-max min-w-full gap-1.5"
        >
          {SITE_SECTION_TABS.map(({ id, icon }, index) => (
            <button
              key={id}
              ref={(element) => {
                refs.current[id] = element;
              }}
              type="button"
              role="tab"
              id={`site-section-tab-${id}`}
              aria-selected={active === id}
              aria-controls={`site-section-${id}`}
              tabIndex={active === id ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(id)}
              onKeyDown={(event) => move(event, index)}
              className={pillClass(active === id)}
            >
              <i className={`bi ${icon}`} aria-hidden="true" />
              {t(`Dashboard.MySite.sections.${id}`)}
            </button>
          ))}
        </div>
      </div>

      {more.start && <EdgeArrow side="start" onClick={() => page(-1)} />}
      {more.end && <EdgeArrow side="end" onClick={() => page(1)} />}
    </div>
  );
};

SiteSectionTabs.propTypes = {
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default SiteSectionTabs;
